import type { ResultOf } from '@graphql-typed-document-node/core'
import { GET_COLLECTION_ENTRIES, GET_FILTERED_COLLECTION_ENTRIES } from '@/queries'
import {
  GET_FILTERED_CUSTOM_LIST_NOVELTIES,
  GET_FILTERED_CUSTOM_LIST_RELEASE_ENTRIES,
} from '@/queries/customLists'
import { mapNoveltyItem } from '@/views/overview/components/config'
import {
  CollectionFilterProps,
  type CollectionListConfig,
  type ListConfig,
  normalizeSearchInput,
  type NoveltyCustomListConfig,
  ReleaseEntrySorts,
} from '@/utils'
import type { CustomListStats } from '@/stores/lists'
import type { LocalizationStore } from '@/stores/localization'
import {
  NoveltyRelationTypeType,
  OrderDirection,
  SeriesInfoTypeType,
  UserReleaseEntryStatusOrderByInput,
  UserReleaseEntryStatusWhereInput,
} from '../../../__generated__/graphql.ts'
import { LineBuilderConfig, type PreviewEntryData } from '@/components/PreviewEntry.vue'
import { getVolumeNumbersFromVersions } from '@/stores/user-collection'

type CustomMetricConfig = CollectionListConfig | NoveltyCustomListConfig

// A user list shows metrics only if it is custom (built-in tabs use getListStats).
export function asCustomMetricConfig(config: ListConfig): CustomMetricConfig | null {
  if (config.category === 'novelty-custom') return config
  if ((config.category === 'collection' || config.category === 'read') && config.isCustom) {
    return config
  }
  return null
}

const PENDING_METRIC = '–'

export function customListMetricsLine(
  config: ListConfig,
  stats: CustomListStats | undefined,
  t: LocalizationStore['t'],
  formatPrice: LocalizationStore['formatPrice'],
): string | null {
  const custom = asCustomMetricConfig(config)
  if (!custom || (!custom.showTotalNumber && !custom.showTotalValue)) return null
  const parts: string[] = []
  if (custom.showTotalNumber) {
    parts.push(
      custom.category === 'novelty-custom'
        ? t({
            key: 'listMetricNoveltyCount',
            context: { count: stats?.itemCount ?? PENDING_METRIC },
          })
        : t({
            key: 'listMetricReleaseEntryCount',
            context: {
              series: stats?.seriesCount ?? PENDING_METRIC,
              volumes: stats?.volumeCount ?? PENDING_METRIC,
            },
          }),
    )
  }
  if (custom.showTotalValue) {
    parts.push(
      t({
        key: 'listMetricValue',
        context: { value: stats ? formatPrice(stats.totalValue) : PENDING_METRIC },
      }),
    )
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

export type ListSubtitleDeps = {
  customStats: CustomListStats | undefined
  collectionStats: { series: number; volumes: number; released: number } | null
  collectionLoaded: boolean
  t: LocalizationStore['t']
  tListLabel: LocalizationStore['tListLabel']
  formatPrice: LocalizationStore['formatPrice']
}

export function listHasSubtitleSlot(
  config: ListConfig,
  collectionStats: { series: number; volumes: number; released: number } | null,
): boolean {
  const custom = asCustomMetricConfig(config)
  if (custom && (custom.showTotalNumber || custom.showTotalValue)) return true
  return collectionStats !== null
}

export function listSubtitleLine(config: ListConfig, deps: ListSubtitleDeps): string {
  const metricsLine = customListMetricsLine(config, deps.customStats, deps.t, deps.formatPrice)
  if (metricsLine) return metricsLine
  const stats = deps.collectionStats
  if (stats) {
    if (!deps.collectionLoaded) {
      return deps.t({
        key: 'listStatsLine',
        context: { series: PENDING_METRIC, volumes: PENDING_METRIC, released: PENDING_METRIC },
      })
    }
    if (stats.series === 0) return ''
    if (stats.volumes === 0) {
      return deps.t({ key: 'listSeriesLine', context: { series: stats.series } })
    }
    return deps.t({
      key: 'listStatsLine',
      context: { series: stats.series, volumes: stats.volumes, released: stats.released },
    })
  }
  if (config.category !== 'database') return ''
  const subtitle = config.subtitle
  if (!subtitle) return ''
  return deps.tListLabel(subtitle)
}

export const horizontalListBuilder: LineBuilderConfig = {
  preTitleLine: () => '',
  titleLine: (item) => item.localizedSeriesTitle,
  postTitleLine: () => '',
}

export const listsLineBuilder: LineBuilderConfig = {
  preTitleLine: (item, { t }) => {
    return t('publisherLine', {
      publisher: item.publisherName,
    })
  },
  titleLine: (item) => item.localizedSeriesTitle,
  postTitleLine: (item, { t }) => {
    if (item.volumeCount !== undefined) {
      return t('releaseEntryVolumeAmountLineWithType', {
        count: item.totalVolumeCount,
        type: t(item.type),
      })
    }
    return ''
  },
}

export const getPaginationItemsForCollectionEntries = (
  data: ResultOf<typeof GET_COLLECTION_ENTRIES>,
): PreviewEntryData[] => {
  return (
    data?.userReleaseEntryStatuses?.map((e) => ({
      localizedSeriesTitle: e.internalName,
      volumeCount:
        getVolumeNumbersFromVersions(e.entry?.sortedVolumeNumbers).length -
        getVolumeNumbersFromVersions(e.entry?.notReleasedVolumeNumbers).length,
      totalVolumeCount: getVolumeNumbersFromVersions(e.entry?.sortedVolumeNumbers).length,
      type: (e.entry?.type as unknown as SeriesInfoTypeType) || SeriesInfoTypeType.Manga,
      id: e.id,
      entryId: e.entry?.id,
      publisherName: e.publisherName,
      cover: e.entry?.cover,
      thumbHash: e.entry?.thumbHash,
      relationType: NoveltyRelationTypeType.Volume,
      relationId: e.id,
    })) || []
  )
}

export const getSortForCollections = (
  sorts: ReleaseEntrySorts,
): { orderBy: UserReleaseEntryStatusOrderByInput[] } => {
  return {
    orderBy: [
      ...sorts
        .filter((s) => s.order != null)
        .map((s) => ({
          [s.property]: s.order,
        })),
      {
        id: OrderDirection.Asc,
      },
    ],
  }
}

export const getWhereForCollectionReleaseEntries = (
  filter: CollectionFilterProps,
): { where: UserReleaseEntryStatusWhereInput; filterMode: string | null } => {
  const tagsFilter = filter.selectedTags
    ? filter.selectedTags.map((tagId) => ({
        tagSearchField: { contains: `${tagId}` },
      }))
    : []
  const demographyFilter: UserReleaseEntryStatusWhereInput[] = filter.selectedDemographies
    ? filter.selectedDemographies.map((demographyId) => ({
        demographySearchField: { contains: `${demographyId}` },
      }))
    : []
  const excludedTagsFilter: UserReleaseEntryStatusWhereInput[] = filter.excludedTags
    ? filter.excludedTags.map((tagId) => ({
        tagSearchField: { contains: `${tagId}` },
      }))
    : []
  const excludedDemographyFilter: UserReleaseEntryStatusWhereInput[] = filter.excludedDemographies
    ? filter.excludedDemographies.map((demographyId) => ({
        demographySearchField: { contains: `${demographyId}` },
      }))
    : []
  const normalizedSearchInput = filter.seriesTitle
    ? normalizeSearchInput(filter.seriesTitle).map((part) => ({ titleSearch: { contains: part } }))
    : []
  const hasSelectedMedium = !!filter.selectedMedium && filter.selectedMedium.length > 0
  const hasExcludedMedium = !!filter.excludedMedium && filter.excludedMedium.length > 0
  const typeFilter =
    hasSelectedMedium || hasExcludedMedium
      ? {
          ...(hasSelectedMedium ? { in: filter.selectedMedium } : {}),
          ...(hasExcludedMedium ? { notIn: filter.excludedMedium } : {}),
        }
      : null
  const hasSelectedPublisher = !!filter.selectedPublisher && filter.selectedPublisher.length > 0
  const hasExcludedPublisher = !!filter.excludedPublisher && filter.excludedPublisher.length > 0
  const publisherFilter =
    hasSelectedPublisher || hasExcludedPublisher
      ? {
          ...(hasSelectedPublisher ? { in: filter.selectedPublisher } : {}),
          ...(hasExcludedPublisher ? { notIn: filter.excludedPublisher } : {}),
        }
      : null
  const ANDConditions: UserReleaseEntryStatusWhereInput[] = [
    ...(filter.startDate ? [{ startDate: { gte: filter.startDate } }] : []),
    ...normalizedSearchInput,
    ...(demographyFilter.length > 0 ? [{ OR: demographyFilter }] : []),
    ...(excludedTagsFilter.length > 0 ? [{ NOT: [{ OR: excludedTagsFilter }] }] : []),
    ...(excludedDemographyFilter.length > 0 ? [{ NOT: [{ OR: excludedDemographyFilter }] }] : []),
  ]
  return {
    where: {
      ...(filter.statusFilter === 'collection'
        ? filter.collectionStatus
          ? { collectionStatus: { equals: filter.collectionStatus } }
          : {}
        : filter.readStatus
          ? { currentReadStatus: { equals: filter.readStatus } }
          : {}),
      ...(filter.excludeNoStartDate ? { startDate: { not: null } } : {}),
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(publisherFilter ? { publisherName: publisherFilter } : {}),
      ...(ANDConditions.length > 0 ? { AND: ANDConditions } : {}),
      ...(tagsFilter.length > 0 ? { OR: tagsFilter } : {}),
    },
    filterMode: filter.collectionFilterMode ?? null,
  }
}

export const getPaginationItemsForCustomListNovelties = (
  data: ResultOf<typeof GET_FILTERED_CUSTOM_LIST_NOVELTIES>,
): PreviewEntryData[] => (data?.filteredCustomListNovelties ?? []).map(mapNoveltyItem)

export const getPaginationItemsForCustomListReleaseEntries = (
  data: ResultOf<typeof GET_FILTERED_CUSTOM_LIST_RELEASE_ENTRIES>,
): PreviewEntryData[] =>
  (data?.filteredReleaseEntries ?? []).map((e) => ({
    localizedSeriesTitle: e.internalName,
    volumeCount:
      getVolumeNumbersFromVersions(e.sortedVolumeNumbers).length -
      getVolumeNumbersFromVersions(e.notReleasedVolumeNumbers).length,
    totalVolumeCount: getVolumeNumbersFromVersions(e.sortedVolumeNumbers).length,
    type: (e.type as unknown as SeriesInfoTypeType) || SeriesInfoTypeType.Manga,
    id: e.id,
    entryId: e.id,
    publisherName: e.publisherName,
    cover: e.cover,
    thumbHash: e.thumbHash,
    relationType: NoveltyRelationTypeType.Volume,
    relationId: e.id,
  }))

export const getPaginationItemsForFilteredCollectionEntries = (
  data: ResultOf<typeof GET_FILTERED_COLLECTION_ENTRIES>,
): PreviewEntryData[] => {
  return (
    data?.filteredUserReleaseEntryStatuses?.map((e) => ({
      localizedSeriesTitle: e.internalName,
      volumeCount:
        getVolumeNumbersFromVersions(e.entry?.sortedVolumeNumbers).length -
        getVolumeNumbersFromVersions(e.entry?.notReleasedVolumeNumbers).length,
      totalVolumeCount: getVolumeNumbersFromVersions(e.entry?.sortedVolumeNumbers).length,
      type: (e.entry?.type as unknown as SeriesInfoTypeType) || SeriesInfoTypeType.Manga,
      id: e.id,
      entryId: e.entry?.id,
      publisherName: e.publisherName,
      cover: e.entry?.cover,
      thumbHash: e.entry?.thumbHash,
      relationType: NoveltyRelationTypeType.Volume,
      relationId: e.id,
    })) || []
  )
}
